// The new-customer form (QA-323): one form for Calls (the caller's number is known), Book's customer
// field, Messages and Riverstone's Customers "Add customer" (Blueprint's Customers draws it with ui-web's
// NewCustomerFields, BL-189). What it does (the duplicate notice that never blocks, the check again on
// saving, the key that adds a customer once) is use-new-customer.ts's; this is how it's drawn.

import { useId, type ReactNode } from 'react';
import { SaveButton } from './SaveButton.tsx';
import { type CustomerForm } from './new-customer-model.ts';
import { useNewCustomer } from './use-new-customer.ts';
import './new-customer.css';

export function NewCustomerForm({
  phone,
  start,
  who = 'them',
  consentNote,
  submitLabel,
  link,
  pick = 'Use',
  onDone,
  onCancel,
}: {
  /** A call's number (E.164): the form asks for no mobile then, and texting is asked on the call. */
  phone?: string;
  start?: CustomerForm;
  /** Who the duplicate notice is about ("this caller"). */
  who?: string;
  consentNote: string;
  submitLabel: ReactNode;
  /** Runs once the customer exists (or an existing one is picked), before onDone: e.g. link the call. */
  link?: (customerId: string, idempotencyKey?: string) => Promise<unknown>;
  /** What picking the possible duplicate does: use them (book, link the call) or open them (Customers). */
  pick?: 'Use' | 'Open';
  onDone: (customerId: string) => void;
  onCancel?: () => void;
}) {
  const nc = useNewCustomer({
    ...(phone ? { phone } : {}),
    ...(start ? { start } : {}),
    who,
    ...(link ? { link } : {}),
    pick,
    onDone,
  });
  const f = nc.form;
  const { busy, error, held, dupe } = nc;
  const consentId = useId();
  const set =
    (k: keyof CustomerForm) => (e: { target: { value: string; checked?: boolean; type?: string } }) =>
      nc.change({ [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const check = () => nc.check();
  const create = (anyway = false) => nc.create(anyway);

  return (
    <div className="nc-form">
      <div className="fgrid">
        <label className="f">
          Name
          <input className="field" value={f.name} onChange={set('name')} autoFocus />
        </label>
        {!phone && (
          <label className="f">
            Mobile
            <input
              className="field"
              type="tel"
              value={f.mobile}
              onChange={set('mobile')}
              onBlur={check}
              placeholder="optional"
            />
          </label>
        )}
        <label className="f">
          Email
          <input
            className="field"
            type="email"
            value={f.email}
            onChange={set('email')}
            onBlur={check}
            placeholder="optional"
          />
        </label>
        <label className="f">
          Service address
          <input className="field" value={f.line1} onChange={set('line1')} onBlur={check} />
        </label>
        <label className="f">
          City
          <input className="field" value={f.city} onChange={set('city')} onBlur={check} />
        </label>
        <label className="f">
          Type
          <select className="field" value={f.kind} onChange={set('kind')}>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>
        </label>
      </div>
      <div className="nc-consent" role="group" aria-labelledby={consentId}>
        <div className="flabel" id={consentId}>
          {consentNote}
        </div>
        <label className="checkrow">
          <input
            type="checkbox"
            checked={f.sms}
            onChange={set('sms')}
            disabled={!phone && !f.mobile.trim()}
          />{' '}
          OK to text about their appointments and invoices
        </label>
        <label className="checkrow">
          <input type="checkbox" checked={f.mail} onChange={set('mail')} disabled={!f.email.trim()} /> OK to
          email
        </label>
      </div>
      {dupe && (
        <div className="notice warn nc-dupe" role={held ? 'alert' : undefined}>
          <p>{dupe.text}</p>
          <div className="row">
            <button className="btn soft" disabled={busy} onClick={dupe.pick}>
              {dupe.pickLabel}
            </button>
            {held && (
              <button className="btn quiet" disabled={busy} onClick={() => create(true)}>
                Add anyway
              </button>
            )}
          </div>
        </div>
      )}
      {nc.uncheckedText && (
        <div className="notice warn nc-dupe" role="alert">
          <p>{nc.uncheckedText}</p>
          <button className="btn quiet" disabled={busy} onClick={() => create(true)}>
            Add anyway
          </button>
        </div>
      )}
      <div className="row">
        <SaveButton
          busy={busy}
          busyLabel="Adding the customer…"
          error={error}
          disabled={nc.blocked}
          onClick={() => create()}
        >
          {submitLabel}
        </SaveButton>
        {onCancel && (
          <button className="btn quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
